import * as React from "react";
import { CheckIcon, Circle, HelpCircle, LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type Criteria = {
  key: KeyType;
  label: string;
  type: string; // "number" | "percentage"
  icon: LucideIcon;
  description: string;
  quota_id?: number;
};

export const criterias: Criteria[] = [
  // {
  //   key: {
  //     key_name: "attainment_period",
  //   },
  //   type: "percentage",
  //   label: "Target attainment for period",
  //   icon: HelpCircle,
  // },
  // {
  //   key: {
  //     key_name: "attainment_ytd",
  //   },
  //   type: "percentage",
  //   label: "Target attainment year to date",
  //   icon: HelpCircle,
  // },
  {
    key: {
      key_name: "deal_providers",
    },
    type: "string",
    label: "Deal providers",
    description: "Where the deals are located",
    icon: HelpCircle,
  },
  {
    key: {
      key_name: "deal_types",
    },
    type: "string",
    label: "Deal types",
    description: "What types of deals to compensate for",
    icon: HelpCircle,
  },
  {
    key: {
      key_name: "deal_role",
    },
    type: "string",
    label: "Deal role",
    description: "What role the user must have for the deals",
    icon: HelpCircle,
  },
  {
    key: {
      key_name: "deals_contract_length_months",
    },
    type: "number",
    label: "Deal contract length",
    description: "Contract length in months",
    icon: HelpCircle,
  },
  {
    key: {
      key_name: "deals_invoice_length_months",
    },
    type: "number",
    label: "Deal invoice period",
    description: "Invoice period in months",
    icon: Circle,
  },
  {
    key: {
      key_name: "deals_size_value",
    },
    type: "number",
    label: "Deal size",
    description: "Value of deal in SEK",
    icon: Circle,
  },
  {
    key: {
      key_name: "deals_size_arr_value",
    },
    type: "number",
    label: "Deal size ARR",
    description: "Annual recurring value of deal in SEK",
    icon: Circle,
  },
  {
    key: {
      key_name: "deals_customer_deal_count",
    },
    type: "number",
    label: "Deal count",
    description: "Number of deals for the same customer",
    icon: Circle,
  },
  {
    key: {
      key_name: "deals_pipeline",
    },
    type: "string",
    label: "Deal pipeline",
    description: "The pipeline the deal is in",
    icon: Circle,
  },
  {
    key: {
      key_name: "deals_label",
    },
    type: "string",
    label: "Deal label",
    description: "The label of the deal",
    icon: Circle,
  },
  {
    key: {
      key_name: "deals_owner",
    },
    type: "string",
    label: "Deal owner",
    description: "The owner of the deal",
    icon: Circle,
  },
  {
    key: {
      key_name: "deals_collaborator",
    },
    type: "string",
    label: "Deal collaborator",
    description: "The collaborator of the deal",
    icon: Circle,
  },
  {
    key: {
      key_name: "hs_custom_dealtype",
    },
    type: "string",
    label: "HS custom dealtype",
    description: "Hubspot custom dealtype",
    icon: Circle,
  },
  {
    key: {
      key_name: "hs_custom_term_over_billing",
    },
    type: "string",
    label: "HS custom Term/Billing",
    description: "(Tendium) Hubspot custom Term/Billing",
    icon: Circle,
  },
  {
    key: {
      key_name: "custom",
    },
    type: "string",
    label: "Custom",
    description: "Custom formula",
    icon: Circle,
  },
];

type KeyType = {
  key_name?: string;
  quota_id?: number;
};

export function FilterCombobox({
  value: _,
  onChange,
}: {
  value: KeyType;
  onChange: (value: KeyType) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [selectedCriteria, setSelectedCriteria] = React.useState<
    Criteria | undefined
  >(undefined);
  const allCriterias = criterias;

  const handleSelect = (label: string) => {
    const selectedCriteria = allCriterias.find((c) => c.label === label);
    setSelectedCriteria(selectedCriteria);
    setOpen(false);
    onChange({
      key_name: selectedCriteria?.key.key_name || "",
      quota_id: selectedCriteria?.quota_id
        ? selectedCriteria?.quota_id
        : undefined,
    });
  };

  return (
    <div className="flex items-center space-x-4">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="w-[350px] justify-start"
          >
            {selectedCriteria ? (
              <>
                {selectedCriteria.label}
              </>
            ) : (
              <>Set criteria</>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[350px]" side="right" align="start">
          <Command>
            <CommandInput placeholder="Search filters..." />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {allCriterias.map((criteria) => (
                  <CommandItem
                    key={criteria.key.key_name}
                    value={criteria.label}
                    onSelect={(label) => handleSelect(label)}
                  >
                    <div className="flex items-start gap-3 text-muted-foreground">
                      <div className="grid gap-0.5">
                        <p>
                          <span className="font-medium text-foreground">
                            {criteria.label}
                          </span>
                        </p>
                        <p className="text-xs" data-description>
                          {criteria.description}
                        </p>
                      </div>
                    </div>
                    <CheckIcon
                      className={cn(
                        "ml-auto my-auto h-6 w-6",
                        criteria.key.key_name === selectedCriteria?.key.key_name
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
